'use client';

import { useEffect, useState } from 'react';

import { useOnCheckoutEnded } from '../../checkout/notification/useOnCheckoutEnded';
import { hasChannelSubscriptionAccess } from '../../community-channels/functions/access/hasChannelSubscriptionAccess';
import { useJoinChannel } from '../../community-channels/hooks/useJoinChannel';
import type { CommunityChannel } from '../../community-channels/types/CommunityChannel';
import type { CommunityChannelMember } from '../../community-channels/types/CommunityChannelMember';
import { useBioUserAccess } from '../contexts/user-access/useBioUserAccess';

/**
 * Auto-joins a gated channel once a creator-subscription checkout is dismissed —
 * the web port of PopshopRN's `onSubscribed` continuation in
 * `useJoinCommunityChannel`. Without it, a visitor who subscribes from the join
 * gate returns to a gate that merely flipped to "Join Channel Now!" and has to
 * tap again; upstream drops them straight into the channel.
 *
 * Flow: on the `ended` phase of a `community_membership_subscription` checkout
 * (the buyer closed the success screen), re-resolve the viewer's standing and
 * arm a pending join. Once the refreshed level meets the channel's requirement
 * and the viewer isn't already a member, join in place — the live membership doc
 * then swaps the pane in. If they subscribed BELOW the channel bar, nothing
 * fires (the gate stays on "Subscribe & Join"), matching upstream's level
 * re-check.
 */
export function useAutoJoinChannelAfterSubscription(
  channel: CommunityChannel | null | undefined,
  membership: CommunityChannelMember | null | undefined,
): void {
  const { extendedSubscriptionLevel, loading, refresh } = useBioUserAccess();
  const { join } = useJoinChannel(channel);
  const [pendingJoin, setPendingJoin] = useState(false);

  useOnCheckoutEnded(event => {
    if (event.type !== 'community_membership_subscription') return;
    setPendingJoin(true);
    // The subscription just finalized — re-read the viewer's standing so the
    // level check below sees the new tier.
    refresh();
  });

  useEffect(() => {
    if (!pendingJoin || channel == null) return;
    // Wait for the refreshed subscription standing + the membership doc.
    if (loading || membership === undefined) return;
    // Already a member (the join landed, or they joined elsewhere) — disarm.
    if (membership !== null) {
      setPendingJoin(false);
      return;
    }
    // Subscribed below the channel bar — nothing to auto-join. Stay armed (a
    // no-op) so a later higher-tier subscribe still lands them in.
    if (
      !hasChannelSubscriptionAccess(
        extendedSubscriptionLevel,
        channel.minimumSubscriptionLevel,
      )
    ) {
      return;
    }
    setPendingJoin(false);
    void join();
  }, [
    pendingJoin,
    channel,
    membership,
    loading,
    extendedSubscriptionLevel,
    join,
  ]);
}
